import { action, observable, runInAction } from "mobx";

import type { DesktopApi, SetupRequest, SetupUpdate } from "../desktop.ts";
import type { ConnectionStore } from "./ConnectionStore.ts";

/** Setting up a new server, in the desktop app. */
export class SetupStore {
    @observable accessor status: "idle" | "running" | "done" | "reset" | "failed" = "idle";
    @observable accessor progress: Extract<SetupUpdate, { type: "progress" }> | undefined = undefined;
    @observable accessor log: string[] = [];
    /** Why it stopped, when status is "reset" or "failed". */
    @observable accessor reason = "";

    #desktop: DesktopApi | undefined;
    #connection: ConnectionStore;

    constructor(desktop: DesktopApi | undefined, connection: ConnectionStore) {
        this.#desktop = desktop;
        this.#connection = connection;

        // Without a controller yet, the list of models comes from the desktop app. Without it, you type a name.
        desktop?.models().then(
            (models) => connection.useModels(models),
            (error: Error) => console.warn(`Couldn't get the list of models: ${error.message}`),
        );
    }

    @action async start(request: SetupRequest): Promise<void> {
        if (!this.#desktop) throw new Error("Setting up a server only works in the desktop app.");
        this.status = "running";
        this.progress = undefined;
        this.log = [];
        this.reason = "";
        await this.#desktop.setup(request, (update) => runInAction(() => this.#apply(update, request)));

        runInAction(() => {
            if (this.status === "running") {
                this.status = "failed";
                this.reason = "The setup stopped without saying why.";
            }
        });
    }

    /** Back to the form, unless a setup is running. */
    @action reset(): void {
        if (this.status !== "running") this.status = "idle";
    }

    cancel(): void {
        this.#desktop?.cancelSetup();
    }

    #apply(update: SetupUpdate, request: SetupRequest): void {
        switch (update.type) {
            case "progress":
                this.progress = update;
                this.log.push(`▸ ${update.message}`);
                break;
            case "log":
                this.log.push(update.text);
                break;

            case "done": {
                this.status = "done";
                const { llm } = request;

                this.#connection.save({
                    url: update.url,
                    password: request.mainionsPassword,
                    llmUrl: llm.url,
                    llmKey: llm.key ?? "",
                    llmModel: llm.model,
                });

                break;
            }

            case "reset":
            case "failed":
                this.status = update.type;
                this.reason = update.type === "reset" ? update.reason : update.error;
                break;
        }
    }
}
